import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Cookie, X } from 'lucide-react';

const CONSENT_KEY = 'cookie_consent';

type ConsentStatus = 'pending' | 'accepted' | 'declined';

export const getCookieConsent = (): ConsentStatus => {
  if (typeof window === 'undefined') return 'pending';
  const stored = localStorage.getItem(CONSENT_KEY);
  if (stored === 'accepted' || stored === 'declined') return stored;
  return 'pending';
};

export const hasAnalyticsConsent = (): boolean => {
  return getCookieConsent() === 'accepted';
};

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Small delay to avoid flash on page load
    const timer = setTimeout(() => {
      if (getCookieConsent() === 'pending') {
        setVisible(true);
      }
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  const handleAccept = () => {
    localStorage.setItem(CONSENT_KEY, 'accepted');
    setVisible(false);
    
    // Enable Meta Pixel tracking after consent
    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('consent', 'grant');
      (window as any).fbq('track', 'PageView');
    }
  };

  const handleDecline = () => {
    localStorage.setItem(CONSENT_KEY, 'declined');
    setVisible(false);
    
    // Revoke Meta Pixel consent
    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('consent', 'revoke');
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6">
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-slate-200 p-4 md:p-6">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
          {/* Icon */}
          <div className="hidden md:flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-blue-100 to-purple-100 flex-shrink-0">
            <Cookie className="w-6 h-6 text-blue-600" />
          </div>
          
          {/* Content */}
          <div className="flex-1">
            <h3 className="font-semibold text-slate-900 mb-1">
              We value your privacy 🍪
            </h3>
            <p className="text-sm text-slate-600 leading-relaxed">
              We use cookies to enhance your browsing experience, serve personalized ads, 
              and analyze our traffic. By clicking "Accept All", you consent to our use of cookies.{' '}
              <a 
                href="/privacy" 
                className="text-blue-600 hover:text-blue-700 underline underline-offset-2"
              >
                Learn more
              </a>
            </p>
          </div>
          
          {/* Actions */}
          <div className="flex items-center gap-3 w-full md:w-auto flex-shrink-0">
            <Button
              variant="outline"
              onClick={handleDecline}
              className="flex-1 md:flex-none border-slate-300 text-slate-700 hover:bg-slate-100"
            >
              Decline
            </Button>
            <Button
              onClick={handleAccept}
              className="flex-1 md:flex-none bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
            >
              Accept All
            </Button>
          </div>
          
          {/* Mobile close */}
          <button
            onClick={handleDecline}
            className="absolute top-3 right-3 md:hidden p-1 text-slate-400 hover:text-slate-600"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
